/**
 * The leaderboard API (server/main.py). One POST per finished round, one GET per board.
 * Offline-friendly: a score that can't be sent waits in localStorage and goes next time.
 */
const API = (import.meta.env.VITE_API as string | undefined) ?? 'https://taco.lunaloo.net/api';
const PENDING = 'labu.pending';

export interface Entry {
  player: string;
  name: string;
  score: number;
  mistakes: number;
  at: number;
}

export interface Board {
  game: string;
  entries: Entry[];
  /** this player's standing, if they have posted */
  me: { best: number; rank: number; players: number } | null;
}

export interface ScorePost {
  game: string;
  player: string;
  name: string;
  score: number;
  mistakes: number;
  detail?: Record<string, unknown>;
}

async function post(body: ScorePost): Promise<Board> {
  const r = await fetch(`${API}/v1/scores`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`${r.status}`);
  return (await r.json()) as Board;
}

function pending(): ScorePost[] {
  try {
    return (JSON.parse(localStorage.getItem(PENDING) ?? '[]') as ScorePost[]) ?? [];
  } catch {
    return [];
  }
}

function setPending(list: ScorePost[]): void {
  try {
    localStorage.setItem(PENDING, JSON.stringify(list.slice(-50)));
  } catch {
    /* no storage */
  }
}

/** post a round; null when offline (it is queued) */
export async function submitScore(body: ScorePost): Promise<Board | null> {
  try {
    const board = await post(body);
    void flushPending();
    return board;
  } catch (e) {
    // a 4xx means the server rejected it (bad score); only network failures are queued
    if (e instanceof Error && /^4\d\d$/.test(e.message)) return null;
    setPending([...pending(), body]);
    return null;
  }
}

/** send anything that couldn't be sent earlier */
export async function flushPending(): Promise<void> {
  const list = pending();
  if (!list.length) return;
  const left: ScorePost[] = [];
  for (const s of list) {
    try {
      await post(s);
    } catch (e) {
      if (!(e instanceof Error && /^4\d\d$/.test(e.message))) left.push(s);
    }
  }
  setPending(left);
}

export async function fetchBoard(game: string, player: string, limit = 25): Promise<Board | null> {
  try {
    const r = await fetch(`${API}/v1/leaderboard/${encodeURIComponent(game)}?player=${encodeURIComponent(player)}&limit=${limit}`);
    if (!r.ok) return null;
    return (await r.json()) as Board;
  } catch {
    return null;
  }
}

/** tell the server the player's new name (their past scores show it too) */
export async function renamePlayer(player: string, name: string): Promise<boolean> {
  try {
    const r = await fetch(`${API}/v1/players/${encodeURIComponent(player)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
    return r.ok;
  } catch {
    return false;
  }
}
